import { OrderDoc, ProductStateCode } from '@gooduncles/gu-app-schema';
import { Image } from 'antd';
import { FC } from 'react';
import { orderStatusKr, userRoleKr } from 'src/constants';

import { formatNumber } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';

import useDeliveryTask from 'src/hooks/subscription/useDeliveryTask';
import useSWRDeliverySpot from 'src/hooks/swr/useSWRDeliverySpot';
import useStoreById from 'src/hooks/swr/useStoreById';
import useUserById from 'src/hooks/swr/useUserById';

import CollapsibleList from 'src/atomic-components/atoms/CollapsibleList/CollapsibleList';
import ListRow from 'src/atomic-components/atoms/ListRow/ListRow';

import classes from './OrderView.module.scss';

interface OrderViewProps {
  order: OrderDoc;
}

const OrderView: FC<OrderViewProps> = ({ order }) => {
  const { _id, userId, storeId, products, orderDate } = order;
  const { user } = useUserById(userId);
  const { store } = useStoreById(storeId);
  const { deliverySpot } = useSWRDeliverySpot(order.deliverySpotId);
  const { deliveryPartnerId } = order;
  const { deliveryTask } = useDeliveryTask(deliveryPartnerId ? `${deliveryPartnerId}-order-${_id}` : undefined);
  const deliveryImages = deliveryTask?.deliveryImages ?? [];

  return (
    <div className={classes.orderViewContent}>
      <CollapsibleList id={_id + orderDate} label='주문 정보' defaultOpen={true}>
        <ListRow title='orderId' detail={_id} />
        <ListRow title='주문 생성 시각' detail={formatDate(orderDate, 'yyyy-MM-dd HH:mm:ss')} />
        <ListRow title='배송일' detail={order.date ?? ''} />
        <ListRow title='주문 상태' detail={orderStatusKr[order.orderStatus]} />
        <ListRow title='총 합계(배송비 포함)' detail={formatNumber(order.grandTotal)} />
      </CollapsibleList>

      <CollapsibleList id={`${_id}-delivery`} label='배송 정보' defaultOpen={true}>
        <ListRow title='배송 담당자' detail={order.deliveryPartnerName ?? '미배정'} />
        <ListRow title='배송지' detail={deliverySpot?.title ?? order.deliverySpotId ?? '미배정'} />
        <ListRow
          title='배송 완료 시각'
          detail={
            order.deliveryTaskFinishedAt ? formatDate(order.deliveryTaskFinishedAt, 'yyyy-MM-dd HH:mm:ss') : '미완료'
          }
        />
        {deliveryImages.length > 0 && (
          <ListRow
            title='배송 완료 이미지'
            detail={
              <Image.PreviewGroup>
                <div className={classes.deliveryImages}>
                  {deliveryImages.map((image, index) => (
                    <Image
                      key={index}
                      width={56}
                      height={56}
                      className={classes.deliveryImage}
                      src={image.thumbnail.url}
                      preview={{ src: image.original.url }}
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            }
          />
        )}
      </CollapsibleList>

      {store && (
        <CollapsibleList id={storeId} label='매장 정보' defaultOpen={true}>
          <ListRow title='매장명' detail={store.storeNickname} />
          {/* <ListRow title='배송지' detail={store.deliverySpotId} /> */}
        </CollapsibleList>
      )}

      {user && (
        <CollapsibleList id={userId} label='주문자 정보' defaultOpen={true}>
          <ListRow title='이메일' detail={user.email} />
          <ListRow title='전화번호' detail={user.userTel} />
          <ListRow title='계정 등급' detail={userRoleKr[user.role]} />
        </CollapsibleList>
      )}

      <CollapsibleList id={_id} label='주문 상품' defaultOpen={true}>
        <ListRow key='deliveryFee' title='배송비' detail={formatNumber(order.deliveryFee ?? 0)} />
        {products.map((product) => {
          const isOutOfStock = product.state === ProductStateCode.OUTOFSTOCK;
          return (
            <ListRow
              key={product.productId}
              title={
                <span
                  style={{
                    textDecoration: isOutOfStock ? 'line-through' : 'none',
                  }}
                >
                  {product.fullName}
                </span>
              }
              subtitle={
                <>
                  <span>{`${formatNumber(product.price)} x ${product.volume ?? 1}`}</span>
                  {isOutOfStock && <span className={classes.outOfStock}>품절</span>}
                </>
              }
              detail={formatNumber(product.price * (product.volume ?? 1))}
            />
          );
        })}
      </CollapsibleList>
    </div>
  );
};

export default OrderView;
